import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, ArrowLeft, Loader2, Copy, Download, Target, 
  ClipboardList, Lightbulb, BookOpen, CheckSquare, BarChart3, Type, FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORKSHEET_TYPES = [
  { 
    id: 'progress_monitoring', 
    name: 'Progress Monitoring',
    icon: BarChart3,
    description: 'Data collection tools to track IEP goal progress',
    color: 'text-blue-600 bg-blue-100'
  },
  { 
    id: 'practice', 
    name: 'Practice Worksheet',
    icon: FileText,
    description: 'Skill-building activities aligned to the goal',
    color: 'text-green-600 bg-green-100'
  },
  { 
    id: 'project', 
    name: 'Project-Based Learning',
    icon: Lightbulb,
    description: 'Engaging hands-on projects that practice goal skills',
    color: 'text-purple-600 bg-purple-100'
  },
  { 
    id: 'task_analysis', 
    name: 'Task Analysis',
    icon: ClipboardList,
    description: 'Step-by-step breakdown for ADL and functional skills',
    color: 'text-orange-600 bg-orange-100'
  },
];

const GOAL_DOMAINS = [
  'Reading', 'Math', 'Written Language', 'Communication', 
  'Social Skills', 'Emotional Regulation', 'Executive Function',
  'Motor Skills', 'Activities of Daily Living', 'Behavior'
];

const WorksheetGenerator = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savedGoals, setSavedGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inputMode, setInputMode] = useState('prompt'); // 'prompt' or 'goal'
  
  const [formData, setFormData] = useState({
    goal_id: '',
    goal_text: '',
    goal_domain: '',
    worksheet_type: '',
    student_name: '',
    grade_level: ''
  });

  const gradeOptions = [
    'ECE (Early Childhood)', 'Kindergarten', 
    '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade', 
    '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'YA (Young Adult 18-22)'
  ];

  useEffect(() => {
    fetchSavedGoals();
  }, []);

  const fetchSavedGoals = async () => {
    try {
      const response = await axios.get(`${API}/goal-bank`, { withCredentials: true });
      setSavedGoals(response.data);
    } catch (error) {
      console.error('Error fetching saved goals:', error);
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on input mode
    if (inputMode === 'goal' && !formData.goal_id) {
      toast.error('Please select a goal from your Goal Bank');
      return;
    }
    if (inputMode === 'prompt' && !formData.goal_text.trim()) {
      toast.error('Please enter a skill or goal description');
      return;
    }
    if (!formData.worksheet_type) {
      toast.error('Please select a worksheet type');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const requestData = {
        worksheet_type: formData.worksheet_type,
        student_name: formData.student_name || null,
        grade_level: formData.grade_level || null
      };
      
      if (inputMode === 'goal') {
        requestData.goal_id = formData.goal_id;
      } else {
        requestData.goal_text = formData.goal_text;
        requestData.goal_domain = formData.goal_domain || null;
      }
      
      const response = await axios.post(`${API}/generate-worksheet`, requestData, {
        withCredentials: true
      });
      setResult(response.data);
      toast.success('Worksheet generated!');
    } catch (error) {
      console.error('Error generating worksheet:', error);
      toast.error('Failed to generate worksheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    
    const text = formatResultAsText(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatResultAsText = (data) => {
    const ws = data.worksheet;
    let text = '';
    
    if (data.worksheet_type === 'progress_monitoring') {
      text = `PROGRESS MONITORING: ${ws.title || ws.worksheet_title}\n\n`;
      text += `Goal: ${ws.goal_simplified || ws.goal_summary || ''}\n`;
      text += `Method: ${ws.data_collection_method || ''}\n\n`;
      text += `SUCCESS CRITERIA: ${ws.success_criteria || ''}\n\n`;
      
      // Handle new format with problems/reading_passage/practice_items
      if (ws.problems?.length > 0) {
        text += `PROBLEMS:\n`;
        ws.problems.forEach(p => {
          text += `${p.problem_number}. ${p.problem_text}\n\n`;
        });
      } else if (ws.reading_passage) {
        text += `READING PASSAGE: ${ws.reading_passage.title}\n\n`;
        text += `${ws.reading_passage.text}\n\n`;
        if (ws.comprehension_questions?.length > 0) {
          text += `COMPREHENSION QUESTIONS:\n`;
          ws.comprehension_questions.forEach(q => {
            text += `${q.question_number}. ${q.question_text}\n`;
          });
        }
      } else if (ws.practice_items?.length > 0) {
        text += `PRACTICE ITEMS:\n`;
        ws.practice_items.forEach(item => {
          text += `${item.item_number}. ${item.item_text}\n\n`;
        });
      }
      
      // Legacy format
      ws.worksheet_sections?.forEach(section => {
        text += `--- ${section.section_name} ---\n`;
        text += `Instructions: ${section.instructions}\n`;
        text += `${section.content}\n\n`;
      });
      text += `WHAT TO LOOK FOR:\n`;
      ws.observation_prompts?.forEach(p => text += `• ${p}\n`);
      if (ws.graphing_tip) text += `\nGRAPHING TIP: ${ws.graphing_tip}`;
    } else if (data.worksheet_type === 'project') {
      text = `PROJECT: ${ws.project_title}\n\n`;
      text += `Connection to Goal: ${ws.goal_connection}\n`;
      text += `Overview: ${ws.project_overview}\n\n`;
      text += `MATERIALS:\n`;
      ws.materials_needed?.forEach(m => text += `• ${m}\n`);
      text += `\nSTEPS:\n`;
      ws.steps?.forEach(step => {
        text += `\n${step.step_number}. ${step.step_name}\n`;
        text += `   ${step.description}\n`;
        text += `   Skill Practice: ${step.skill_practice}\n`;
        text += `   Modifications: ${step.modifications}\n`;
      });
      text += `\nASSESSMENT IDEAS:\n`;
      ws.assessment_ideas?.forEach(a => text += `• ${a}\n`);
      text += `\nREAL-WORLD CONNECTION: ${ws.real_world_connection}`;
    } else if (data.worksheet_type === 'task_analysis') {
      text = `TASK ANALYSIS: ${ws.task_title}\n\n`;
      text += `Category: ${ws.skill_category || ''}\n\n`;
      if (ws.materials_needed?.length > 0) {
        text += `MATERIALS NEEDED:\n`;
        ws.materials_needed.forEach(m => text += `• ${m}\n`);
      }
      text += `\nSTEPS:\n`;
      ws.steps?.forEach(step => {
        text += `\n${step.step_number}. ${step.action}\n`;
        if (step.visual_cue) text += `   Look for: ${step.visual_cue}\n`;
        if (step.verbal_prompt) text += `   Say: "${step.verbal_prompt}"\n`;
      });
      if (ws.data_collection) {
        text += `\nDATA COLLECTION:\n`;
        text += `Method: ${ws.data_collection.method}\n`;
        text += `Scoring: ${ws.data_collection.scoring}\n`;
        text += `Mastery: ${ws.data_collection.mastery_criteria}\n`;
      }
      if (ws.reinforcement_ideas?.length > 0) {
        text += `\nREINFORCEMENT IDEAS:\n`;
        ws.reinforcement_ideas.forEach(r => text += `• ${r}\n`);
      }
    } else {
      text = `${ws.title}\n\n`;
      text += `Instructions: ${ws.instructions}\n\n`;
      text += `EXAMPLE:\n${ws.example_problem}\n\n`;
      text += `PRACTICE:\n`;
      ws.practice_items?.forEach(item => {
        text += `\n${item.item_number}. ${item.prompt}\n`;
        if (item.hint) text += `   Hint: ${item.hint}\n`;
      });
      text += `\nSELF-CHECK: ${ws.self_check}`;
      if (ws.bonus_challenge) text += `\n\nBONUS: ${ws.bonus_challenge}`;
    }
    
    return text;
  };

  // Download worksheet as PDF
  const downloadWorksheetPdf = () => {
    if (!result?.worksheet) return;
    
    const ws = result.worksheet;
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...darkSage);
    const title = ws.worksheet_title || ws.title || ws.task_title || ws.project_title || 'Worksheet';
    pdf.text(title, pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 15;
    
    // Student info line
    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Name: ${formData.student_name || '_________________'}`, margin, yPos);
    pdf.text(`Date: _________________`, pageWidth - margin - 50, yPos);
    yPos += 10;
    
    // Instructions
    const instructions = ws.instructions_for_student || ws.instructions || '';
    if (instructions) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...darkSage);
      pdf.text('Instructions:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const instrLines = pdf.splitTextToSize(instructions, contentWidth);
      pdf.text(instrLines, margin, yPos);
      yPos += instrLines.length * 4 + 8;
    }
    
    // Separator
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    // Content based on type
    if (result.worksheet_type === 'task_analysis' && ws.steps?.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Task Steps', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      
      ws.steps.forEach((step, i) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        // Step number and action
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${step.step_number}. ${step.action}`, margin, yPos);
        yPos += 5;
        
        // Visual cue
        if (step.visual_cue) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          const cueLines = pdf.splitTextToSize(`Look for: ${step.visual_cue}`, contentWidth - 10);
          pdf.text(cueLines, margin + 5, yPos);
          yPos += cueLines.length * 4;
        }
        
        // Data collection checkbox
        pdf.setDrawColor(150, 150, 150);
        pdf.rect(pageWidth - margin - 30, yPos - 10, 8, 8);
        pdf.setFontSize(7);
        pdf.text('Done', pageWidth - margin - 20, yPos - 5);
        
        yPos += 6;
        pdf.setFontSize(10);
        pdf.setTextColor(40, 40, 40);
      });
      
      // Data collection guide
      if (ws.data_collection) {
        yPos += 10;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...darkSage);
        pdf.text('Scoring:', margin, yPos);
        yPos += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(60, 60, 60);
        const scoringLines = pdf.splitTextToSize(ws.data_collection.scoring || '', contentWidth);
        pdf.text(scoringLines, margin, yPos);
      }
    } else if (ws.problems?.length > 0) {
      // Math worksheet
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Problems', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.problems.forEach((p) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const problemText = `${p.problem_number}. ${p.problem_text}`;
        const pLines = pdf.splitTextToSize(problemText, contentWidth - 10);
        pLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else if (ws.reading_passage) {
      // Reading worksheet
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text(ws.reading_passage.title || 'Reading Passage', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      const passageLines = pdf.splitTextToSize(ws.reading_passage.text || '', contentWidth);
      
      passageLines.forEach((line) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      
      // Questions
      if (ws.comprehension_questions?.length > 0) {
        yPos += 10;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(...darkSage);
        pdf.text('Comprehension Questions', margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(40, 40, 40);
        
        ws.comprehension_questions.forEach((q) => {
          if (yPos > pageHeight - 25) {
            pdf.addPage();
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            yPos = margin;
          }
          
          const qLines = pdf.splitTextToSize(`${q.question_number}. ${q.question_text}`, contentWidth - 5);
          qLines.forEach((line) => {
            pdf.text(line, margin, yPos);
            yPos += 5;
          });
          pdf.text(`Answer: ________________________________________`, margin + 5, yPos);
          yPos += 12;
        });
      }
    } else if (ws.practice_items?.length > 0) {
      // Practice items
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Practice Items', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.practice_items.forEach((item) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const itemText = `${item.item_number}. ${item.item_text || item.prompt}`;
        const itemLines = pdf.splitTextToSize(itemText, contentWidth - 10);
        itemLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else if (result.worksheet_type === 'project' && ws.steps?.length > 0) {
      // Project steps
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Project Steps', margin, yPos);
      yPos += 8;
      
      ws.steps.forEach((step) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(...darkSage);
        pdf.text(`Step ${step.step_number}: ${step.step_name}`, margin, yPos);
        yPos += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(60, 60, 60);
        const descLines = pdf.splitTextToSize(step.description, contentWidth);
        pdf.text(descLines, margin, yPos);
        yPos += descLines.length * 4 + 8;
      });
    }
    
    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    const typeLabel = result.worksheet_type === 'task_analysis' ? 'Task_Analysis' :
                      result.worksheet_type === 'project' ? 'Project' :
                      result.worksheet_type === 'progress_monitoring' ? 'Progress_Monitoring' :
                      'Worksheet';
    pdf.save(`${typeLabel}_${formData.student_name?.replace(/\s+/g, '_') || 'Student'}.pdf`);
    toast.success('PDF downloaded!');
  };

  const selectedGoal = savedGoals.find(g => g.goal_id === formData.goal_id);

  return (
    <div className="min-h-screen bg-background" data-testid="worksheet-generator-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          <Button
            onClick={() => navigate('/iep-goals')}
            variant="ghost"
            className="text-text-secondary hover:text-primary"
          >
            <Target className="w-4 h-4 mr-2" />
            IEP Goals
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-8 max-w-5xl mx-auto">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-text-secondary hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-text-primary mb-2">
            <ClipboardList className="w-8 h-8 inline mr-3 text-[#6366F1]" />
            Worksheet & Project Generator
          </h1>
          <p className="font-body text-text-secondary">
            Create progress monitoring tools and engaging activities from a text prompt or your saved IEP goals
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Generate Resources</CardTitle>
              <CardDescription className="font-body">
                Enter a skill description or select from your Goal Bank
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Input Mode Tabs */}
                <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="prompt" className="flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Text Prompt
                    </TabsTrigger>
                    <TabsTrigger value="goal" className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4" />
                      Saved Goal
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="prompt" className="mt-4 space-y-4">
                    <div>
                      <Label className="font-body font-semibold">Skill or Goal Description *</Label>
                      <Textarea
                        value={formData.goal_text}
                        onChange={(e) => setFormData({ ...formData, goal_text: e.target.value })}
                        placeholder="e.g., Student will add two-digit numbers with regrouping with 80% accuracy"
                        className="mt-2 min-h-[80px]"
                        data-testid="goal-text-input"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Describe the skill you want to practice or assess
                      </p>
                    </div>
                    <div>
                      <Label className="font-body font-semibold">Skill Domain (Optional)</Label>
                      <Select 
                        value={formData.goal_domain} 
                        onValueChange={(v) => setFormData({ ...formData, goal_domain: v })}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select domain..." />
                        </SelectTrigger>
                        <SelectContent>
                          {GOAL_DOMAINS.map((domain) => (
                            <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="goal" className="mt-4">
                    <div>
                      <Label className="font-body font-semibold">Select Goal from Goal Bank</Label>
                      <Select 
                        value={formData.goal_id} 
                        onValueChange={(v) => setFormData({ ...formData, goal_id: v })}
                      >
                        <SelectTrigger className="mt-2" data-testid="goal-select">
                          <SelectValue placeholder={loadingGoals ? "Loading goals..." : "Choose a saved goal"} />
                        </SelectTrigger>
                        <SelectContent>
                          {savedGoals.map((goal) => (
                            <SelectItem key={goal.goal_id} value={goal.goal_id}>
                              {goal.title} ({goal.goal_domain})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savedGoals.length === 0 && !loadingGoals && (
                        <p className="text-xs text-text-muted mt-2">
                          No saved goals yet. <button type="button" onClick={() => navigate('/iep-goals')} className="text-[#6366F1] underline">Create and save goals first</button>, or use the Text Prompt tab.
                        </p>
                      )}
                    </div>

                    {/* Show selected goal preview */}
                    {selectedGoal && (
                      <div className="bg-secondary/30 rounded-lg p-3 text-sm mt-3">
                        <p className="font-semibold text-text-primary">{selectedGoal.title}</p>
                        <p className="text-text-secondary mt-1 text-xs line-clamp-2">
                          {selectedGoal.annual_goal?.goal_text || 'No goal text'}
                        </p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Worksheet Type */}
                <div>
                  <Label className="font-body font-semibold">Resource Type</Label>
                  <div className="grid gap-3 mt-2">
                    {WORKSHEET_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.worksheet_type === type.id;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, worksheet_type: type.id })}
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected 
                              ? 'border-[#6366F1] bg-[#6366F1]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`type-${type.id}`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <p className="font-heading font-semibold text-text-primary">{type.name}</p>
                            <p className="text-xs text-text-muted mt-0.5">{type.description}</p>
                          </div>
                          {isSelected && (
                            <CheckSquare className="w-5 h-5 text-[#6366F1]" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Optional fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body font-semibold">Student Name (Optional)</Label>
                    <Input
                      value={formData.student_name}
                      onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                      placeholder="For personalization"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label className="font-body font-semibold">Grade Level (Optional)</Label>
                    <Select 
                      value={formData.grade_level} 
                      onValueChange={(v) => setFormData({ ...formData, grade_level: v })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeOptions.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || (!formData.goal_id && !formData.goal_text.trim()) || !formData.worksheet_type}
                  className="w-full bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full py-6"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Generate Resource
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Result Section */}
          <div>
            {loading && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-[#6366F1] mx-auto mb-4" />
                  <p className="font-body text-text-secondary">Creating your resource...</p>
                </CardContent>
              </Card>
            )}

            {result && !loading && (
              <Card data-testid="result-card">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="font-heading">
                        {result.worksheet_type === 'progress_monitoring' && '📊 Progress Monitoring'}
                        {result.worksheet_type === 'practice' && '📝 Practice Worksheet'}
                        {result.worksheet_type === 'project' && '🎯 Project-Based Learning'}
                        {result.worksheet_type === 'task_analysis' && '📋 Task Analysis'}
                      </CardTitle>
                      <CardDescription className="font-body text-xs mt-1">
                        {result.goal_domain} • Generated just now
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={copyToClipboard}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                      >
                        {copied ? <CheckSquare className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        onClick={downloadWorksheetPdf}
                        size="sm"
                        className="rounded-full bg-[#6366F1] text-white hover:bg-[#6366F1]/90"
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
                  {/* Task Analysis Result */}
                  {result.worksheet_type === 'task_analysis' && result.worksheet && (
                    <>
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h3 className="font-heading font-bold text-lg text-orange-800">
                          {result.worksheet.task_title}
                        </h3>
                        <p className="text-sm text-orange-700 mt-1">
                          Category: {result.worksheet.skill_category}
                        </p>
                      </div>
                      
                      {result.worksheet.materials_needed?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-text-primary mb-2">Materials Needed</h4>
                          <div className="flex flex-wrap gap-2">
                            {result.worksheet.materials_needed.map((m, i) => (
                              <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3">Task Steps</h4>
                        {result.worksheet.steps?.map((step, i) => (
                          <div key={i} className="border-l-4 border-orange-300 pl-4 mb-4">
                            <p className="font-semibold text-text-primary">
                              Step {step.step_number}: {step.action}
                            </p>
                            {step.visual_cue && (
                              <p className="text-sm text-text-secondary mt-1">
                                <span className="font-medium">Look for:</span> {step.visual_cue}
                              </p>
                            )}
                            {step.verbal_prompt && (
                              <p className="text-xs text-orange-600 mt-1 italic">
                                Say: "{step.verbal_prompt}"
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {result.worksheet.data_collection && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800">Data Collection</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            <strong>Method:</strong> {result.worksheet.data_collection.method}
                          </p>
                          <p className="text-sm text-blue-700 mt-1">
                            <strong>Scoring:</strong> {result.worksheet.data_collection.scoring}
                          </p>
                          <p className="text-sm text-blue-700 mt-1">
                            <strong>Mastery:</strong> {result.worksheet.data_collection.mastery_criteria}
                          </p>
                        </div>
                      )}
                      
                      {result.worksheet.reinforcement_ideas?.length > 0 && (
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800">Reinforcement Ideas</h4>
                          <ul className="text-sm text-green-700 mt-1 space-y-1">
                            {result.worksheet.reinforcement_ideas.map((r, i) => (
                              <li key={i}>• {r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {/* Progress Monitoring Result */}
                  {result.worksheet_type === 'progress_monitoring' && result.worksheet && (
                    <>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h3 className="font-heading font-bold text-lg text-blue-800">
                          {result.worksheet.title || result.worksheet.worksheet_title}
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">
                          {result.worksheet.goal_simplified || result.worksheet.goal_summary}
                        </p>
                        {result.worksheet.data_collection_method && (
                          <p className="text-xs text-blue-600 mt-2">
                            Method: {result.worksheet.data_collection_method}
                          </p>
                        )}
                      </div>
                      
                      {/* New format: Math problems */}
                      {result.worksheet.problems?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-text-primary mb-3">Problems</h4>
                          {result.worksheet.problems.map((p, i) => (
                            <div key={i} className="border rounded-lg p-4 mb-3">
                              <p className="font-medium text-text-primary">
                                {p.problem_number}. {p.problem_text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* New format: Reading passage */}
                      {result.worksheet.reading_passage && (
                        <div>
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-text-primary mb-2">
                              {result.worksheet.reading_passage.title}
                            </h4>
                            <p className="text-sm text-text-secondary whitespace-pre-wrap">
                              {result.worksheet.reading_passage.text}
                            </p>
                          </div>
                          
                          {result.worksheet.comprehension_questions?.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-text-primary mb-3">Comprehension Questions</h4>
                              {result.worksheet.comprehension_questions.map((q, i) => (
                                <div key={i} className="border rounded-lg p-4 mb-3">
                                  <p className="font-medium text-text-primary">
                                    {q.question_number}. {q.question_text}
                                  </p>
                                  <p className="text-xs text-text-muted mt-1">
                                    Type: {q.question_type}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* New format: General practice items */}
                      {result.worksheet.practice_items?.length > 0 && !result.worksheet.problems && !result.worksheet.reading_passage && (
                        <div>
                          <h4 className="font-semibold text-text-primary mb-3">Practice Items</h4>
                          {result.worksheet.practice_items.map((item, i) => (
                            <div key={i} className="border rounded-lg p-4 mb-3">
                              <p className="font-medium text-text-primary">
                                {item.item_number}. {item.item_text}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Legacy format: worksheet_sections */}
                      {result.worksheet.worksheet_sections?.map((section, i) => (
                        <div key={i} className="border rounded-lg p-4">
                          <h4 className="font-semibold text-text-primary">{section.section_name}</h4>
                          <p className="text-sm text-text-muted mt-1">{section.instructions}</p>
                          <div className="bg-gray-50 rounded p-3 mt-3 text-sm whitespace-pre-wrap font-mono">
                            {section.content}
                          </div>
                        </div>
                      ))}
                      
                      {result.worksheet.success_criteria && (
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800">Success Criteria</h4>
                          <p className="text-sm text-green-700">{result.worksheet.success_criteria}</p>
                        </div>
                      )}
                      
                      {result.worksheet.observation_prompts?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-text-primary mb-2">Observation Prompts</h4>
                          <ul className="space-y-1">
                            {result.worksheet.observation_prompts.map((p, i) => (
                              <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                                <span className="text-blue-500">•</span> {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {/* Project Result */}
                  {result.worksheet_type === 'project' && result.worksheet && (
                    <>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h3 className="font-heading font-bold text-lg text-purple-800">
                          {result.worksheet.project_title}
                        </h3>
                        <p className="text-sm text-purple-700 mt-1">{result.worksheet.project_overview}</p>
                        <p className="text-xs text-purple-600 mt-2">
                          Goal Connection: {result.worksheet.goal_connection}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-text-primary mb-2">Materials Needed</h4>
                        <div className="flex flex-wrap gap-2">
                          {result.worksheet.materials_needed?.map((m, i) => (
                            <span key={i} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3">Project Steps</h4>
                        {result.worksheet.steps?.map((step, i) => (
                          <div key={i} className="border-l-4 border-purple-300 pl-4 mb-4">
                            <p className="font-semibold text-text-primary">
                              Step {step.step_number}: {step.step_name}
                            </p>
                            <p className="text-sm text-text-secondary mt-1">{step.description}</p>
                            <p className="text-xs text-purple-600 mt-2">
                              Skill Practice: {step.skill_practice}
                            </p>
                            <p className="text-xs text-text-muted mt-1">
                              Modifications: {step.modifications}
                            </p>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-800">Real-World Connection</h4>
                        <p className="text-sm text-yellow-700">{result.worksheet.real_world_connection}</p>
                      </div>
                    </>
                  )}

                  {/* Practice Worksheet Result */}
                  {result.worksheet_type === 'practice' && result.worksheet && (
                    <>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h3 className="font-heading font-bold text-lg text-green-800">
                          {result.worksheet.title}
                        </h3>
                        <p className="text-sm text-green-700 mt-1">{result.worksheet.instructions}</p>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800">Example</h4>
                        <p className="text-sm text-blue-700 whitespace-pre-wrap mt-1">
                          {result.worksheet.example_problem}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-text-primary mb-3">Practice Items</h4>
                        {result.worksheet.practice_items?.map((item, i) => (
                          <div key={i} className="border rounded-lg p-4 mb-3">
                            <p className="font-medium text-text-primary">
                              {item.item_number}. {item.prompt}
                            </p>
                            {item.hint && (
                              <p className="text-xs text-text-muted mt-2 italic">
                                Hint: {item.hint}
                              </p>
                            )}
                            <div className="bg-gray-50 h-12 rounded mt-2 flex items-center justify-center text-gray-400 text-sm">
                              [Answer space]
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-800">Self-Check</h4>
                        <p className="text-sm text-yellow-700">{result.worksheet.self_check}</p>
                      </div>
                      
                      {result.worksheet.bonus_challenge && (
                        <div className="bg-purple-50 rounded-lg p-4">
                          <h4 className="font-semibold text-purple-800">Bonus Challenge</h4>
                          <p className="text-sm text-purple-700">{result.worksheet.bonus_challenge}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {!result && !loading && (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-text-primary mb-2">
                    Ready to Generate
                  </h3>
                  <p className="font-body text-text-muted text-sm">
                    Select a goal and resource type, then click generate to create your worksheet or project
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WorksheetGenerator;
